import Image from "next/image"
import type { LucideIcon } from "lucide-react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

export interface EmptyStateProps {
  /**
   * Category id with a `public/categories/<id>.webp` product cutout — renders
   * the same foil-tinted medallion as the shop-by-category tiles /
   * coming-soon page. PREFERRED over `icon` whenever the surface is scoped to
   * a category that has a cutout (`CATEGORY_PHOTO_IDS` — the CALLER checks
   * membership and passes the slug or leaves it undefined).
   * @see .tondocs/ui/EMPTY_STATES.md
   */
  photoSlug?: string
  /** Lucide fallback for surfaces with no category cutout (trending, offers…). */
  icon?: LucideIcon
  title: string
  description?: string
  /**
   * Optional primary action. `foil: true` ONLY when this is the surface's one
   * possible action (FOIL.md rule) — e.g. "Sell an item" on an empty category
   * page. Default renders the quiet outline pill.
   */
  action?: { label: string; onClick: () => void; foil?: boolean }
  className?: string
}

/**
 * EmptyState — the ONE way to say "there is nothing here" on a listing
 * surface. Medallion (category cutout, or icon fallback) + title + optional
 * description + optional pill CTA, centered. Stateless primitive: copy,
 * media and the action come from the caller (i18n'd `emptyState.*` keys) —
 * never hardcoded here. Auth gating of the action belongs to the CALLER
 * (sell-hero-section pattern: signed-in → creation flow, else AuthModal).
 *
 * Canonical consumers: `FeedFilteredGrid` (category / trending /
 * open-to-offers pages), `discover-category-panel`. Reuse this instead of a
 * bare centered `<p>` — that pattern is what this component retires.
 * @see .tondocs/ui/EMPTY_STATES.md
 */
export function EmptyState({
  photoSlug,
  icon: Icon,
  title,
  description,
  action,
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        "flex w-full flex-col items-center justify-center gap-4 px-6 py-16 text-center",
        className,
      )}
    >
      {photoSlug ? (
        <div className="category-stage relative flex size-20 items-center justify-center overflow-hidden rounded-2xl border border-black/10 shadow-lg shadow-black/20 dark:border-white/10">
          <div className="absolute" style={{ left: "0.5rem", right: "0.5rem", top: "0.5rem", bottom: "0.5rem" }}>
            {/* `unoptimized`: serve-ready static asset + the optimizer's AVIF
                encoder hangs on some variants of these exact files (see
                shop-by-category.tsx). Literal template src per the
                fitness-next-image-params local-asset convention. */}
            <Image
              src={`/categories/${photoSlug}.webp`}
              alt=""
              aria-hidden
              fill
              unoptimized
              className="object-contain drop-shadow-[0_14px_16px_rgba(0,0,0,0.55)]"
            />
          </div>
        </div>
      ) : Icon ? (
        <div className="flex size-16 items-center justify-center rounded-full bg-muted/60 dark:bg-white/6">
          <Icon className="size-7 text-muted-foreground" strokeWidth={1.5} />
        </div>
      ) : null}
      <div className="space-y-1.5">
        <p className="text-base font-semibold tracking-[-0.01em] text-foreground">
          {title}
        </p>
        {description ? (
          <p className="mx-auto max-w-xs text-sm leading-relaxed text-muted-foreground dark:text-white/50">
            {description}
          </p>
        ) : null}
      </div>
      {action ? (
        <Button
          type="button"
          variant={action.foil ? "foil" : "outline"}
          size="sm"
          className="mt-1 rounded-full px-5"
          onClick={action.onClick}
        >
          {action.label}
        </Button>
      ) : null}
    </div>
  )
}
